import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    LinearProgress,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    TextField,
    Typography,
} from '@mui/material'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

const departmentsSeed = [
    { name: 'Operations', head: 'Mohamed F Sesay', employees: 18, budget: 42600 },
    { name: 'Finance', head: 'Aminata Kamara', employees: 6, budget: 18800 },
    { name: 'Field Team', head: 'Ibrahim Conteh', employees: 34, budget: 51400 },
]

const destinationRules = [
    { label: 'MojoPayr Wallet', fee: 'Free', note: 'Instant internal salary payout' },
    { label: 'Bank Account', fee: '1%', note: 'Settlement routed to selected bank' },
    { label: 'Mobile Money', fee: 'Partner cost', note: 'Pass-through mobile money rail' },
    { label: 'Cross-border Staff', fee: 'Send-money fee', note: 'Uses diaspora pricing when paying into Africa' },
]

const tabs = [
    'Dashboard',
    'Employees',
    'Payroll',
    'Attendance',
    'Leave',
    'Imports',
    'Settings',
]

const payrollCurrencies = ['SLE', 'LRD', 'GNF', 'GMD', 'USD', 'GBP', 'EUR', 'CAD', 'AUD']

const destinationTypes = [
    { value: 'mojopayr_wallet', label: 'MojoPayr Wallet' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'bank', label: 'Bank Account' },
]

const money = (value, currency = 'SLE') =>
    `${currency} ${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

function Metric({ icon, label, value, note }) {
    return (
        <Box sx={shellStyles.softPanel}>
            <Stack direction="row" spacing={1.4} alignItems="center">
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 3,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'rgba(255,138,0,0.13)',
                        color: '#FF8A00',
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 800 }}>
                        {label}
                    </Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>
                        {value}
                    </Typography>
                </Box>
            </Stack>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 1.3 }}>
                {note}
            </Typography>
        </Box>
    )
}

function MiniPanel({ title, children, action }) {
    return (
        <Box sx={{ ...shellStyles.softPanel, p: 2.2 }}>
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                <Typography sx={{ color: '#fff', fontWeight: 900 }}>{title}</Typography>
                {action}
            </Stack>
            {children}
        </Box>
    )
}

export default function MerchantPayrollWorkforce() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [tab, setTab] = useState(0)
    const [employees, setEmployees] = useState([])
    const [batches, setBatches] = useState([])
    const [wallets, setWallets] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [runningPayroll, setRunningPayroll] = useState(false)
    const [apiError, setApiError] = useState('')
    const [query, setQuery] = useState('')
    const [autoPayroll, setAutoPayroll] = useState(true)
    const [approvalRequired, setApprovalRequired] = useState(true)
    const [payrollLocked, setPayrollLocked] = useState(false)
    const [selectedEmployee, setSelectedEmployee] = useState(null)
    const [notificationLog, setNotificationLog] = useState([])
    const [walletNotifications, setWalletNotifications] = useState(true)
    const [emailNotifications, setEmailNotifications] = useState(true)
    const [newName, setNewName] = useState('')
    const [newEmail, setNewEmail] = useState('')
    const [newPhone, setNewPhone] = useState('')
    const [newDepartment, setNewDepartment] = useState('Operations')
    const [newJobTitle, setNewJobTitle] = useState('Staff Member')
    const [newSalary, setNewSalary] = useState('')
    const [newCurrency, setNewCurrency] = useState('')
    const [newDestinationType, setNewDestinationType] = useState('mojopayr_wallet')
    const [newDestinationProvider, setNewDestinationProvider] = useState('')
    const [newDestinationAccount, setNewDestinationAccount] = useState('')
    const [editForm, setEditForm] = useState(null)
    const [updatingEmployee, setUpdatingEmployee] = useState(false)

    const businessName = user?.business_name || user?.businessName || user?.name || 'MojoPayr Business'
    const defaultCurrency = user?.default_currency || user?.currency || 'SLE'
    const currency = employees[0]?.currency || newCurrency || defaultCurrency

    const normalizeEmployee = (employee) => ({
        id: employee.id,
        employeeNo: employee.employee_no || `EMP-${employee.id}`,
        name: employee.name,
        email: employee.email || '',
        phone: employee.phone || '',
        department: employee.department || 'General',
        role: employee.job_title || 'Staff Member',
        destination: employee.destination || 'MojoPayr Wallet',
        destinationType: employee.destination_type || 'mojopayr_wallet',
        destinationProvider: employee.destination_provider || '',
        destinationAccount: employee.destination_account || '',
        status: employee.status || 'Active',
        salary: Number(employee.base_salary ?? employee.salary ?? 0),
        currency: employee.currency || currency,
        allowance: Number(employee.allowance || 0),
        bonus: Number(employee.bonus || 0),
        overtime: Number(employee.overtime || 0),
        loanDeduction: Number(employee.loan_deduction || 0),
        otherDeduction: Number(employee.other_deduction || 0),
        matchedMojoPayrUser: Boolean(employee.matched_mojopayr_user),
        stub: employee.stub || null,
        attendance: 100,
        leave: 0,
    })

    const loadPayroll = async () => {
        setLoading(true)
        setApiError('')
        try {
            const [employeeResponse, batchResponse] = await Promise.all([
                api.get('/merchant/payroll/employees'),
                api.get('/merchant/payroll/batches'),
            ])
            setEmployees((employeeResponse.data?.data || []).map(normalizeEmployee))
            setBatches(batchResponse.data?.data || [])
            try {
                const walletResponse = await api.get('/wallet/summary')
                const walletRows = walletResponse.data?.wallets || walletResponse.data?.data?.wallets || []
                setWallets(Array.isArray(walletRows) ? walletRows : [])
            } catch (walletError) {
                setWallets([])
            }
        } catch (error) {
            setApiError(error.response?.data?.message || 'Payroll data could not be loaded.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadPayroll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!selectedEmployee) {
            setEditForm(null)
            return
        }

        setEditForm({
            name: selectedEmployee.name || '',
            email: selectedEmployee.email || '',
            phone: selectedEmployee.phone || '',
            department: selectedEmployee.department || 'General',
            job_title: selectedEmployee.role || 'Staff Member',
            base_salary: selectedEmployee.salary || 0,
            currency: selectedEmployee.currency || currency,
            destination_type: selectedEmployee.destinationType || 'mojopayr_wallet',
            destination_provider: selectedEmployee.destinationProvider || '',
            destination_account: selectedEmployee.destinationAccount || '',
            allowance: selectedEmployee.allowance || 0,
            bonus: selectedEmployee.bonus || 0,
            overtime: selectedEmployee.overtime || 0,
            loan_deduction: selectedEmployee.loanDeduction || 0,
            other_deduction: selectedEmployee.otherDeduction || 0,
        })
    }, [selectedEmployee, currency])

    const filteredEmployees = useMemo(() => {
        const normalized = query.trim().toLowerCase()
        if (!normalized) return employees
        return employees.filter((employee) =>
            [employee.name, employee.employeeNo, employee.department, employee.role, employee.destination]
                .join(' ')
                .toLowerCase()
                .includes(normalized)
        )
    }, [employees, query])

    const totalPayroll = useMemo(
        () => employees.reduce((sum, employee) => sum + payStub(employee).gross, 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [employees]
    )
    const totalNetPayroll = useMemo(
        () => employees.reduce((sum, employee) => sum + payStub(employee).net, 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [employees]
    )
    const totalDeductions = useMemo(
        () => employees.reduce((sum, employee) => sum + payStub(employee).totalDeductions, 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [employees]
    )
    const reviewCount = employees.filter((employee) => employee.status === 'Review').length
    const averageAttendance = employees.length
        ? Math.round(employees.reduce((sum, employee) => sum + Number(employee.attendance || 0), 0) / employees.length)
        : 0

    const addEmployee = async () => {
        const salary = Number(newSalary || 0)
        if (!newName.trim() || !(salary > 0)) return
        setSaving(true)
        setApiError('')
        try {
            const response = await api.post('/merchant/payroll/employees', {
                name: newName.trim(),
                email: newEmail.trim() || undefined,
                phone: newPhone.trim() || undefined,
                department: newDepartment,
                job_title: newJobTitle.trim() || 'Staff Member',
                destination_type: newDestinationType,
                destination_provider: newDestinationProvider.trim() || undefined,
                destination_account: newDestinationAccount.trim() || undefined,
                base_salary: salary,
                currency: newCurrency || defaultCurrency,
            })
            setEmployees((current) => [normalizeEmployee(response.data.employee), ...current])
            setNewName('')
            setNewEmail('')
            setNewPhone('')
            setNewJobTitle('Staff Member')
            setNewSalary('')
            setNewDestinationType('mojopayr_wallet')
            setNewDestinationProvider('')
            setNewDestinationAccount('')
        } catch (error) {
            const fields = error.response?.data?.errors
            setApiError(fields ? Object.values(fields).flat().join(' ') : error.response?.data?.message || 'Employee could not be saved.')
        } finally {
            setSaving(false)
        }
    }

    const updateEmployee = async () => {
        if (!selectedEmployee || !editForm || updatingEmployee) return
        setUpdatingEmployee(true)
        setApiError('')
        try {
            const response = await api.patch(`/merchant/payroll/employees/${selectedEmployee.id}`, editForm)
            const updated = normalizeEmployee(response.data.employee)
            setEmployees((current) => current.map((employee) => employee.id === updated.id ? updated : employee))
            setSelectedEmployee(updated)
            recordNotification(`${updated.name} payroll profile updated.`)
        } catch (error) {
            const fields = error.response?.data?.errors
            setApiError(fields ? Object.values(fields).flat().join(' ') : error.response?.data?.message || 'Employee could not be updated.')
        } finally {
            setUpdatingEmployee(false)
        }
    }

    const payrollFee = currency === 'GNF' ? employees.length * 5000 : employees.length * 5
    const payoutTotal = totalNetPayroll + payrollFee
    const payrollWallet = wallets.find((wallet) => String(wallet.currency || '').toUpperCase() === String(currency).toUpperCase())
    const payrollWalletBalance = Number(payrollWallet?.available_balance ?? payrollWallet?.balance ?? 0)
    const walletSummary = wallets
        .map((wallet) => `${String(wallet.currency || 'USD').toUpperCase()} ${Number(wallet.available_balance ?? wallet.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
        .join(', ')
    const tabLabel = tabs[tab]

    const recordNotification = (message) => {
        setNotificationLog((current) => [
            {
                id: `note-${Date.now()}`,
                message,
                createdAt: new Date().toLocaleString(),
            },
            ...current,
        ].slice(0, 5))
    }

    const runPayrollBatch = async () => {
        if (!employees.length || runningPayroll) return
        setRunningPayroll(true)
        setApiError('')
        try {
            const response = await api.post('/merchant/payroll/batches/run', {
                pay_period: new Date().toLocaleString(undefined, { month: 'long', year: 'numeric' }),
                employee_ids: employees.map((employee) => employee.id),
                wallet_notifications: walletNotifications,
                email_notifications: emailNotifications,
                approval_required: approvalRequired,
            })
            recordNotification(response.data?.message || `Payroll processed for ${employees.length} employees.`)
            await loadPayroll()
        } catch (error) {
            const fields = error.response?.data?.errors
            setApiError(fields ? Object.values(fields).flat().join(' ') : error.response?.data?.message || 'Payroll could not be processed.')
        } finally {
            setRunningPayroll(false)
        }
    }

    const notifyPayslip = (employee) => {
        if (!employee) return
        const stub = payStub(employee)
        recordNotification(
            `Payslip notice queued for ${employee.name}: net pay ${money(stub.net, employee.currency)}.`
        )
    }

    function payStub(employee) {
        const base = Number(employee?.salary || 0)
        if (employee?.stub) {
            return {
                base: Number(employee.stub.base_salary || 0),
                allowance: Number(employee.stub.allowance || 0),
                bonus: Number(employee.stub.bonus || 0),
                overtime: Number(employee.stub.overtime || 0),
                gross: Number(employee.stub.gross_pay || 0),
                incomeTax: Number(employee.stub.paye_tax || 0),
                pension: Number(employee.stub.pension || 0),
                loan: Number(employee.stub.loan_deduction || 0),
                other: Number(employee.stub.other_deduction || 0),
                totalDeductions: Number(employee.stub.total_deductions || 0),
                net: Number(employee.stub.net_pay || 0),
            }
        }
        const allowance = Number(employee?.allowance || 0)
        const bonus = Number(employee?.bonus || 0)
        const overtime = Number(employee?.overtime || 0)
        const gross = base + allowance + bonus + overtime
        const incomeTax = gross * 0.05
        const pension = gross * 0.025
        const loan = Number(employee?.loanDeduction || 0)
        const other = Number(employee?.otherDeduction || 0)
        const totalDeductions = incomeTax + pension + loan + other
        const net = gross - totalDeductions

        return {
            base,
            allowance,
            bonus,
            overtime,
            gross,
            incomeTax,
            pension,
            loan,
            other,
            totalDeductions,
            net,
        }
    }

    const dashboard = (
        <Stack spacing={2.4}>
            <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                    <Metric
                        icon={<GroupsRoundedIcon />}
                        label="Employees"
                        value={employees.length}
                        note="Active workforce records ready for payroll"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Metric
                        icon={<PaymentsRoundedIcon />}
                        label="Payroll Total"
                        value={money(totalPayroll, currency)}
                        note="Gross salary before tax and pension deductions"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Metric
                        icon={<ScheduleRoundedIcon />}
                        label="Attendance"
                        value={`${averageAttendance}%`}
                        note="Average attendance from current period"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Metric
                        icon={<BadgeRoundedIcon />}
                        label="Needs Review"
                        value={reviewCount}
                        note="Staff records requiring approval"
                    />
                </Grid>
            </Grid>

            <MiniPanel title="Payroll Readiness">
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary' }}>Employee setup</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 800 }}>82%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={82} sx={{ height: 8, borderRadius: 999 }} />
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary' }}>Bank/mobile wallet validation</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 800 }}>64%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={64} sx={{ height: 8, borderRadius: 999 }} />
                </Stack>
            </MiniPanel>
        </Stack>
    )

    const employeeTable = (
        <Stack spacing={2.2}>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            {loading && <LinearProgress sx={{ borderRadius: 999 }} />}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                    fullWidth
                    label="Search employees"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <TextField
                    label="Name"
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                />
                <TextField
                    label="Salary"
                    value={newSalary}
                    onChange={(event) => setNewSalary(event.target.value)}
                />
                <Button
                    variant="contained"
                    onClick={addEmployee}
                    disabled={saving}
                    sx={{ bgcolor: '#FF8A00', color: '#0B0620', fontWeight: 900, minWidth: 130 }}
                >
                    {saving ? 'Saving' : 'Add'}
                </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                    fullWidth
                    label="Employee email"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                />
                <TextField
                    fullWidth
                    label="Employee phone"
                    value={newPhone}
                    onChange={(event) => setNewPhone(event.target.value)}
                />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                    fullWidth
                    label="Job title"
                    value={newJobTitle}
                    onChange={(event) => setNewJobTitle(event.target.value)}
                />
                <TextField
                    label="Currency"
                    value={newCurrency || defaultCurrency}
                    onChange={(event) => setNewCurrency(event.target.value)}
                    select
                    SelectProps={{ native: true }}
                    sx={{ minWidth: 130 }}
                >
                    {payrollCurrencies.map((item) => (
                        <option key={item} value={item}>{item}</option>
                    ))}
                </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                    fullWidth
                    label="Salary goes to"
                    value={newDestinationType}
                    onChange={(event) => setNewDestinationType(event.target.value)}
                    select
                    SelectProps={{ native: true }}
                >
                    {destinationTypes.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </TextField>
                <TextField
                    fullWidth
                    label="Bank/mobile provider"
                    value={newDestinationProvider}
                    onChange={(event) => setNewDestinationProvider(event.target.value)}
                    placeholder="Example: Orange Money, UBA"
                />
                <TextField
                    fullWidth
                    label="Wallet, phone, or account"
                    value={newDestinationAccount}
                    onChange={(event) => setNewDestinationAccount(event.target.value)}
                    placeholder="Email, phone, or account number"
                />
            </Stack>
            <TextField
                label="Department"
                value={newDepartment}
                onChange={(event) => setNewDepartment(event.target.value)}
                select
                SelectProps={{ native: true }}
            >
                {departmentsSeed.map((department) => (
                    <option key={department.name} value={department.name}>
                        {department.name}
                    </option>
                ))}
            </TextField>
            <Box sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Destination</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Salary</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!filteredEmployees.length && !loading && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography sx={{ color: 'text.secondary' }}>
                                        No payroll employees yet. Add an employee with salary and wallet contact to start a real payroll run.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {filteredEmployees.map((employee) => (
                            <TableRow
                                key={employee.id}
                                hover
                                onClick={() => setSelectedEmployee(employee)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>
                                    <Typography sx={{ color: '#fff', fontWeight: 800 }}>{employee.name}</Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                        {employee.employeeNo} - {employee.role} - Click for pay stub
                                    </Typography>
                                </TableCell>
                                <TableCell>{employee.department}</TableCell>
                                <TableCell>{employee.destination}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={employee.status}
                                        size="small"
                                        sx={{
                                            color: '#fff',
                                            bgcolor:
                                                employee.status === 'Review'
                                                    ? 'rgba(255,138,0,0.18)'
                                                    : 'rgba(34,197,94,0.18)',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">{money(employee.salary, employee.currency)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Stack>
    )

    const payroll = (
        <Stack spacing={2.2}>
            <Alert severity={payrollLocked ? 'info' : 'warning'}>
                {payrollLocked
                    ? 'This payroll batch is locked for approval and payout preparation.'
                    : 'Review employee destinations and approval rules before locking payroll.'}
            </Alert>
            <MiniPanel title="Employee Notifications">
                <Stack spacing={1.2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>MojoPayr wallet notification when paid</Typography>
                        <Switch checked={walletNotifications} onChange={(event) => setWalletNotifications(event.target.checked)} />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>Email payslip notification when email exists</Typography>
                        <Switch checked={emailNotifications} onChange={(event) => setEmailNotifications(event.target.checked)} />
                    </Stack>
                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                        Employees receive a payroll received alert, and payroll admins receive batch success or failed-payout alerts.
                    </Typography>
                </Stack>
            </MiniPanel>
            <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                    <Metric icon={<PaymentsRoundedIcon />} label="Gross Payroll" value={money(totalPayroll, currency)} note="Employee salary total" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Metric icon={<SettingsRoundedIcon />} label="Deductions" value={money(totalDeductions, currency)} note="PAYE, pension, loan, and other deductions" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Metric icon={<PaymentsRoundedIcon />} label="Wallet Charge" value={money(payoutTotal, currency)} note="Net pay plus payroll platform fee" />
                </Grid>
            </Grid>
            <Alert severity={payrollWalletBalance >= payoutTotal ? 'success' : 'info'}>
                Payroll needs {money(payoutTotal, currency)} from the {currency} business wallet. Current {currency} wallet: {money(payrollWalletBalance, currency)}{walletSummary ? ` (${walletSummary})` : ''}.
            </Alert>
            <MiniPanel title="Destination-Based Pricing">
                <Grid container spacing={1.2}>
                    {destinationRules.map((rule) => (
                        <Grid item xs={12} sm={6} key={rule.label}>
                            <Box sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(255,255,255,0.10)' }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900 }}>{rule.label}</Typography>
                                <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>{rule.fee}</Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{rule.note}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MiniPanel>
            <Button
                variant="contained"
                onClick={() => setPayrollLocked((value) => !value)}
                sx={{ bgcolor: '#FF8A00', color: '#0B0620', fontWeight: 900, borderRadius: 4, py: 1.2 }}
            >
                {payrollLocked ? 'Unlock Payroll Batch' : 'Lock Payroll for Approval'}
            </Button>
            <Button
                variant="outlined"
                onClick={runPayrollBatch}
                disabled={!employees.length || runningPayroll}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)', fontWeight: 900, borderRadius: 4, py: 1.2 }}
            >
                {runningPayroll ? 'Processing Payroll' : 'Run Payroll and Pay Employees'}
            </Button>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            {notificationLog.length > 0 && (
                <Alert severity="success">{notificationLog[0].message}</Alert>
            )}
            {batches.length > 0 && (
                <MiniPanel title="Recent Payroll Batches">
                    <Stack spacing={1}>
                        {batches.slice(0, 4).map((batch) => (
                            <Box key={batch.id} sx={{ p: 1.4, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)' }}>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                    <Box>
                                        <Typography sx={{ color: '#fff', fontWeight: 900 }}>{batch.batch_no}</Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {batch.pay_period} - {batch.employee_count} employees - {batch.status}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>
                                        {money(batch.wallet_charge, batch.currency)}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </MiniPanel>
            )}
        </Stack>
    )

    const attendance = (
        <Stack spacing={1.5}>
            {employees.map((employee) => (
                <Box key={employee.id} sx={shellStyles.softPanel}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800 }}>{employee.name}</Typography>
                        <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>{employee.attendance}%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={employee.attendance} sx={{ height: 8, borderRadius: 999 }} />
                </Box>
            ))}
        </Stack>
    )

    const leave = (
        <Stack spacing={1.5}>
            {employees.map((employee) => (
                <Box key={employee.id} sx={shellStyles.softPanel}>
                    <Stack direction="row" justifyContent="space-between">
                        <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 800 }}>{employee.name}</Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{employee.department}</Typography>
                        </Box>
                        <Chip label={`${employee.leave} leave days`} sx={{ color: '#fff', bgcolor: 'rgba(91,46,255,0.24)' }} />
                    </Stack>
                </Box>
            ))}
        </Stack>
    )

    const imports = (
        <Stack spacing={2}>
            <MiniPanel
                title="Excel Import Center"
                action={<Chip label="CSV/XLSX ready" sx={{ color: '#fff', bgcolor: 'rgba(255,138,0,0.16)' }} />}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                    <Button variant="outlined" startIcon={<UploadRoundedIcon />} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}>
                        Upload Employee File
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadRoundedIcon />} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}>
                        Download Template
                    </Button>
                </Stack>
            </MiniPanel>
            <MiniPanel title="Export Center">
                <Grid container spacing={1.2}>
                    {['Payroll Register', 'Employee List', 'Tax Summary', 'Attendance Report'].map((item) => (
                        <Grid item xs={12} sm={6} key={item}>
                            <Button fullWidth variant="outlined" startIcon={<DownloadRoundedIcon />} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}>
                                {item}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            </MiniPanel>
        </Stack>
    )

    const settings = (
        <Stack spacing={2}>
            <MiniPanel title="Automation">
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>Auto payroll enabled</Typography>
                        <Switch checked={autoPayroll} onChange={(event) => setAutoPayroll(event.target.checked)} />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>Approval required before payout</Typography>
                        <Switch checked={approvalRequired} onChange={(event) => setApprovalRequired(event.target.checked)} />
                    </Stack>
                </Stack>
            </MiniPanel>
            <MiniPanel title="Permissions">
                {['Payroll approver', 'Employee editor', 'Attendance manager', 'Report viewer'].map((item) => (
                    <Stack direction="row" alignItems="center" key={item}>
                        <Checkbox checked sx={{ color: '#FF8A00' }} />
                        <Typography>{item}</Typography>
                    </Stack>
                ))}
            </MiniPanel>
        </Stack>
    )

    const activeContent = {
        Dashboard: dashboard,
        Employees: employeeTable,
        Payroll: payroll,
        Attendance: attendance,
        Leave: leave,
        Imports: imports,
        Settings: settings,
    }[tabLabel]

    return (
        <>
        <MerchantToolShell
            eyebrow={isFr ? 'Portail entreprise' : 'Business portal'}
            title="Payroll & Workforce Management"
            subtitle={`${businessName} can manage employees, departments, payroll runs, attendance, leave, documents, imports, reports, and payout controls from one web workspace.`}
            chips={['Web portal only', 'Bulk payments', 'Audit ready']}
            primaryAction={{
                label: payrollLocked ? 'Batch Locked' : 'Prepare Payroll',
                onClick: () => setTab(2),
            }}
            secondaryAction={{
                label: 'Import Employees',
                onClick: () => setTab(5),
            }}
            left={
                <Stack spacing={2.4}>
                    <Tabs
                        value={tab}
                        onChange={(_, value) => setTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 42,
                            '& .MuiTab-root': { color: 'text.secondary', fontWeight: 900, textTransform: 'none' },
                            '& .Mui-selected': { color: '#FF8A00 !important' },
                            '& .MuiTabs-indicator': { bgcolor: '#FF8A00' },
                        }}
                    >
                        {tabs.map((item) => (
                            <Tab key={item} label={item} />
                        ))}
                    </Tabs>
                    {activeContent}
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <MiniPanel title="Departments">
                        <Stack spacing={1.2}>
                            {departmentsSeed.map((department) => (
                                <Box key={department.name} sx={{ p: 1.5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)' }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ color: '#fff', fontWeight: 900 }}>{department.name}</Typography>
                                        <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>{department.employees}</Typography>
                                    </Stack>
                                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                        Head: {department.head}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                        Budget: {money(department.budget, currency)}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </MiniPanel>
                    <MiniPanel title="Controls">
                        <Stack spacing={1.1}>
                            <Chip onClick={() => setAutoPayroll((value) => !value)} label={autoPayroll ? 'Auto payroll on' : 'Auto payroll off'} sx={{ color: '#fff', bgcolor: 'rgba(91,46,255,0.22)', cursor: 'pointer' }} />
                            <Chip onClick={() => setApprovalRequired((value) => !value)} label={approvalRequired ? 'Approval required' : 'Direct payout allowed'} sx={{ color: '#fff', bgcolor: 'rgba(255,138,0,0.16)', cursor: 'pointer' }} />
                            <Chip onClick={() => setWalletNotifications((value) => !value)} label={walletNotifications ? 'Wallet notices on' : 'Wallet notices off'} sx={{ color: '#fff', bgcolor: 'rgba(59,130,246,0.16)', cursor: 'pointer' }} />
                            <Chip onClick={() => setEmailNotifications((value) => !value)} label={emailNotifications ? 'Email notices on' : 'Email notices off'} sx={{ color: '#fff', bgcolor: 'rgba(20,184,166,0.16)', cursor: 'pointer' }} />
                            <Chip label="0.5% withdrawal rule ready" sx={{ color: '#fff', bgcolor: 'rgba(34,197,94,0.16)' }} />
                        </Stack>
                    </MiniPanel>
                    {notificationLog.length > 0 && (
                        <MiniPanel title="Notification Log">
                            <Stack spacing={1.1}>
                                {notificationLog.map((item) => (
                                    <Box key={item.id} sx={{ p: 1.2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)' }}>
                                        <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>
                                            {item.message}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                            {item.createdAt}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </MiniPanel>
                    )}
                    <Alert severity="info">
                        Mobile users see a portal link only. Payroll runs, Excel uploads, permissions, and bulk payouts should stay on the business web portal.
                    </Alert>
                </Stack>
            }
        />

        <Dialog
                    open={!!selectedEmployee}
                    onClose={() => setSelectedEmployee(null)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: '#140D2D',
                            color: '#fff',
                            borderRadius: 4,
                            border: '1px solid rgba(183,166,255,0.18)',
                        },
                    }}
                >
                    {selectedEmployee && (
                        <>
                            <DialogTitle sx={{ fontWeight: 900 }}>
                                Employee Pay Stub
                            </DialogTitle>
                            <DialogContent>
                                {(() => {
                                    const stub = payStub(selectedEmployee)
                                    const row = (label, value, strong = false) => (
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            spacing={2}
                                            sx={{ py: 0.75 }}
                                        >
                                            <Typography sx={{ color: strong ? '#fff' : 'text.secondary' }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{ color: strong ? '#FF8A00' : '#fff', fontWeight: strong ? 900 : 800 }}>
                                                {value}
                                            </Typography>
                                        </Stack>
                                    )

                                    return (
                                        <Stack spacing={2}>
                                            <Box sx={{ ...shellStyles.softPanel, p: 2 }}>
                                                <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                                                    {selectedEmployee.name}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary' }}>
                                                    {selectedEmployee.employeeNo} - {selectedEmployee.department} - {selectedEmployee.role}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', mt: 0.8 }}>
                                                    Destination: {selectedEmployee.destination}
                                                </Typography>
                                            </Box>

                                            {editForm && (
                                                <Box sx={{ ...shellStyles.softPanel, p: 2 }}>
                                                    <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Edit Payroll Profile</Typography>
                                                    <Grid container spacing={1.2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Employee name"
                                                                value={editForm.name}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Job title"
                                                                value={editForm.job_title}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, job_title: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Email"
                                                                value={editForm.email}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Phone"
                                                                value={editForm.phone}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, phone: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Department"
                                                                value={editForm.department}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, department: event.target.value }))}
                                                                select
                                                                SelectProps={{ native: true }}
                                                            >
                                                                {departmentsSeed.map((department) => (
                                                                    <option key={department.name} value={department.name}>{department.name}</option>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Currency"
                                                                value={editForm.currency}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, currency: event.target.value }))}
                                                                select
                                                                SelectProps={{ native: true }}
                                                            >
                                                                {payrollCurrencies.map((item) => (
                                                                    <option key={item} value={item}>{item}</option>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Base salary"
                                                                value={editForm.base_salary}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, base_salary: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Salary goes to"
                                                                value={editForm.destination_type}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, destination_type: event.target.value }))}
                                                                select
                                                                SelectProps={{ native: true }}
                                                            >
                                                                {destinationTypes.map((item) => (
                                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Provider"
                                                                value={editForm.destination_provider}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, destination_provider: event.target.value }))}
                                                                placeholder="Bank or mobile money provider"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Wallet, phone, or account"
                                                                value={editForm.destination_account}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, destination_account: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Allowance"
                                                                value={editForm.allowance}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, allowance: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Bonus"
                                                                value={editForm.bonus}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, bonus: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Loan deduction"
                                                                value={editForm.loan_deduction}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, loan_deduction: event.target.value }))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Other deduction"
                                                                value={editForm.other_deduction}
                                                                onChange={(event) => setEditForm((current) => ({ ...current, other_deduction: event.target.value }))}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disabled={updatingEmployee}
                                                        onClick={updateEmployee}
                                                        sx={{ mt: 1.5, bgcolor: '#FF8A00', color: '#0B0620', fontWeight: 900, borderRadius: 3 }}
                                                    >
                                                        {updatingEmployee ? 'Saving Changes' : 'Save Employee Changes'}
                                                    </Button>
                                                </Box>
                                            )}

                                            <Box sx={{ ...shellStyles.softPanel, p: 2 }}>
                                                <Typography sx={{ fontWeight: 900, mb: 1 }}>Earnings</Typography>
                                                {row('Base Salary', money(stub.base, selectedEmployee.currency))}
                                                {row('Allowance', money(stub.allowance, selectedEmployee.currency))}
                                                {row('Bonus', money(stub.bonus, selectedEmployee.currency))}
                                                {row('Overtime', money(stub.overtime, selectedEmployee.currency))}
                                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 1 }} />
                                                {row('Gross Pay', money(stub.gross, selectedEmployee.currency), true)}
                                            </Box>

                                            <Box sx={{ ...shellStyles.softPanel, p: 2 }}>
                                                <Typography sx={{ fontWeight: 900, mb: 1 }}>Deductions</Typography>
                                                {row('PAYE / Income Tax', money(stub.incomeTax, selectedEmployee.currency))}
                                                {row('NASSIT / Pension', money(stub.pension, selectedEmployee.currency))}
                                                {row('Loan Deduction', money(stub.loan, selectedEmployee.currency))}
                                                {row('Other Deduction', money(stub.other, selectedEmployee.currency))}
                                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 1 }} />
                                                {row('Total Deductions', money(stub.totalDeductions, selectedEmployee.currency), true)}
                                            </Box>

                                            <Box sx={{ ...shellStyles.softPanel, p: 2 }}>
                                                {row('Net Pay to Employee', money(stub.net, selectedEmployee.currency), true)}
                                                {row('Payroll Status', selectedEmployee.status)}
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={() => notifyPayslip(selectedEmployee)}
                                                sx={{ bgcolor: '#FF8A00', color: '#0B0620', fontWeight: 900, borderRadius: 3 }}
                                            >
                                                Queue Payslip Notification
                                            </Button>
                                        </Stack>
                                    )
                                })()}
                            </DialogContent>
                        </>
                    )}
        </Dialog>
        </>
    )
}
